import { type ReactNode } from "react"

interface InfoCardItem {
  /** Icon for the list item (defaults to green CircleCheck if not provided) */
  icon?: ReactNode
  /** Text content of the list item */
  text: string
}

interface InfoCardProps {
  /** Color theme — blue for informational, orange for commerce/savings */
  theme: "blue" | "orange"
  /** Icon in the card header */
  icon: ReactNode
  /** Card title */
  title: string
  /** List of items with icons */
  items: InfoCardItem[]
  /** Additional classes on the wrapper */
  className?: string
}

export function InfoCard({ theme, icon, title, items, className = "" }: InfoCardProps) {
  const bgClass = theme === "blue" ? "bg-[#0469f0]/[0.04]" : "bg-[#ff9951]/[0.06]"
  const iconBgClass = theme === "blue" ? "bg-[#0469f0]/10" : "bg-[#ff9951]/10"

  return (
    <div className={`${bgClass} rounded-lg p-4 ${className}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-7 h-7 rounded-lg ${iconBgClass} flex items-center justify-center`}>
          {icon}
        </div>
        <h4 className="text-xs font-bold text-gray-900">{title}</h4>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="flex-shrink-0 mt-0.5">{item.icon || <DefaultCheckIcon />}</span>
            <span className="text-xs text-gray-600 leading-snug">{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DefaultCheckIcon() {
  return (
    <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}
